// MiPerfil — Página de perfil corporativa/ejecutiva para Administrador y Docente.
// Estilo institucional (navy dominante, superficies planas, bordes sutiles).
// Reutilizable por rol; adapta etiqueta y datos de organización.
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOGO_TRANSPARENT } from "@/lib/logoData";
import { avatarGroupsForRole } from "@/lib/avatars";
import {
  Mail, Phone, Building2, ShieldCheck, Pencil, KeyRound, Save, X,
  UploadCloud, BadgeCheck, IdCard,
} from "lucide-react";

const ROLE_LABELS = {
  admin: "Administrador",
  franquiciado: "Franquiciado",
  docente: "Docente",
};

const LOGO = LOGO_TRANSPARENT; // v5.7.5 · logo HD

export default function MiPerfil() {
  const { user, refreshUser } = useAuth();
  const [franchiseName, setFranchiseName] = useState("");
  const [editing, setEditing] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.franchise_id) {
      api.get("/franchises").then(({ data }) => {
        const f = (data || []).find((x) => x.franchise_id === user.franchise_id);
        setFranchiseName(f ? f.name : user.franchise_id);
      }).catch(() => setFranchiseName(user.franchise_id));
    } else {
      setFranchiseName("Sede Principal · Matriz");
    }
  }, [user]);

  if (!user) return null;

  const roleLabel = user.is_super_admin ? "Super Administrador" : (ROLE_LABELS[user.role] || user.role);

  return (
    <div className="space-y-6" data-testid="mi-perfil-page">
      {/* Cabecera ejecutiva con branding */}
      <div className="rounded-lg bg-[#1F2547] text-white overflow-hidden border border-[#1F2547]" data-testid="perfil-hero">
        <div className="px-6 py-5 sm:px-8 sm:py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-14 w-36 sm:w-44 rounded-lg bg-white flex items-center justify-center shrink-0 px-3 py-1.5">
              <img src={LOGO} alt="NEUROMATEMÁTICAS JASQ®" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFCC28]">Mi cuenta</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight truncate">Perfil de {roleLabel}</h1>
              <p className="text-xs text-white/70 mt-0.5">NEUROMATEMÁTICAS JASQ® · Plataforma institucional</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(true)} data-testid="perfil-edit-btn"
              className="inline-flex items-center gap-2 bg-white text-[#1F2547] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#FFCC28] transition-colors">
              <Pencil size={14} /> Editar perfil
            </button>
            <button onClick={() => setPwdOpen(true)} data-testid="perfil-password-btn"
              className="inline-flex items-center gap-2 border border-white/30 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-white/10 transition-colors">
              <KeyRound size={14} /> Cambiar contraseña
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Tarjeta de identidad */}
        <section className="lg:col-span-4 bg-white border border-[#E1E5F0] rounded-lg p-6 text-center" data-testid="perfil-identity">
          <div className="relative inline-block">
            <img src={user.avatar} alt={user.name}
              className="w-28 h-28 rounded-lg object-cover bg-[#F7F8FA] border border-[#E1E5F0] mx-auto" data-testid="perfil-avatar" />
            <span className="absolute -bottom-2 -right-2 w-8 h-8 rounded-md bg-[#1F2547] text-white flex items-center justify-center border-2 border-white">
              <BadgeCheck size={16} />
            </span>
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight text-[#1F2547] mt-4">{user.name}</h2>
          <span className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md bg-[#1F2547]/5 text-[#1F2547] border border-[#E1E5F0]" data-testid="perfil-role">
            <ShieldCheck size={12} /> {roleLabel}
          </span>
        </section>

        {/* Datos institucionales */}
        <section className="lg:col-span-8 bg-white border border-[#E1E5F0] rounded-lg overflow-hidden" data-testid="perfil-details">
          <div className="px-6 py-4 border-b border-[#E1E5F0] bg-[#F7F8FA]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6E7591] flex items-center gap-2">
              <IdCard size={13} /> Información de la cuenta
            </p>
          </div>
          <div className="divide-y divide-[#E1E5F0]">
            <Row icon={Mail} label="Correo electrónico" value={user.email} accent="#315197" testid="perfil-email" />
            <Row icon={Phone} label="Teléfono" value={user.phone || "No registrado"} accent="#A43790" testid="perfil-phone" />
            <Row icon={Building2} label="Organización / Franquicia" value={franchiseName || "—"} accent="#FFCC28" iconColor="#9C7A18" testid="perfil-org" />
            <Row icon={ShieldCheck} label="Rol y permisos" value={roleLabel} accent="#3FA84D" testid="perfil-permisos" />
          </div>
        </section>
      </div>

      {editing && (
        <EditModal user={user} onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await refreshUser(); }} />
      )}
      {pwdOpen && <PasswordModal onClose={() => setPwdOpen(false)} />}
    </div>
  );
}

function Row({ icon: Icon, label, value, accent = "#1F2547", iconColor, testid }) {
  return (
    <div className="px-6 py-4 flex items-center gap-4" data-testid={testid}>
      <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
        style={{ background: `${accent}14`, color: iconColor || accent }}>
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6E7591]">{label}</p>
        <p className="text-sm font-semibold text-[#1F2547] truncate">{value}</p>
      </div>
    </div>
  );
}

function EditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", avatar: user.avatar || "", avatar_source: user.avatar_source || null });
  const avatarGroups = avatarGroupsForRole(user.role, user.age);
  const [saving, setSaving] = useState(false);

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("La imagen supera 4 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, avatar: reader.result, avatar_source: "upload" }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio."); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: form.name.trim(), phone: form.phone, avatar: form.avatar, avatar_source: form.avatar_source || "upload" });
      toast.success("Perfil actualizado");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo actualizar el perfil");
    } finally { setSaving(false); }
  };

  // v5.7.5 · el cuadro tiene altura máxima y su cuerpo se desplaza: el botón «Guardar» ya no queda fuera de pantalla
  return (
    <div className="fixed inset-0 z-[60] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4" data-testid="perfil-edit-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md max-h-[92vh] flex flex-col bg-white rounded-lg shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E1E5F0] flex items-center justify-between bg-[#1F2547] text-white">
          <h3 className="font-display text-lg font-bold tracking-tight">Editar mi perfil</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="perfil-edit-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto min-h-0">
          <div className="flex items-center gap-4">
            <img src={form.avatar} alt="avatar" className="w-20 h-20 rounded-lg object-cover bg-[#F7F8FA] border border-[#E1E5F0]" data-testid="perfil-edit-preview" />
            <label className="inline-flex items-center gap-2 border border-[#E1E5F0] text-[#1F2547] text-xs font-semibold px-3 py-2 rounded-md cursor-pointer hover:bg-[#F7F8FA]">
              <UploadCloud size={14} /> Subir foto
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="perfil-avatar-upload" />
            </label>
          </div>
          <div data-testid="perfil-avatar-gallery">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6E7591]">O elige un avatar</span>
            <div className="mt-2 space-y-3 max-h-56 overflow-y-auto pr-1">
              {avatarGroups.map((g) => (
                <div key={g.id}>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#6E7591] mb-1.5">{g.label}</p>
                  <div className="grid grid-cols-5 gap-2">
                    {g.items.map((av) => {
                      const active = form.avatar === av.url;
                      return (
                        <button type="button" key={av.id} data-testid={`perfil-avatar-${av.id}`}
                          onClick={() => setForm((f) => ({ ...f, avatar: av.url, avatar_source: "preset" }))}
                          className={`relative aspect-square rounded-lg border-2 overflow-hidden transition-all ${active ? "border-[#1F2547] ring-2 ring-[#1F2547]/20" : "border-[#E1E5F0] hover:border-[#FFCC28]"}`}>
                          <img src={av.url} alt={av.id} className="w-full h-full object-cover bg-white" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6E7591]">Nombre *</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="perfil-name-input"
              className="mt-1 w-full px-3 py-2 rounded-md border border-[#E1E5F0] focus:border-[#1F2547] outline-none text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6E7591]">Teléfono</span>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="perfil-phone-input"
              className="mt-1 w-full px-3 py-2 rounded-md border border-[#E1E5F0] focus:border-[#1F2547] outline-none text-sm" />
          </label>
        </div>
        <div className="border-t border-[#E1E5F0] px-5 py-3 flex items-center gap-3 shrink-0 bg-white">
          <button onClick={onClose} className="border border-[#E1E5F0] text-[#1F2547] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#F7F8FA]" data-testid="perfil-edit-cancel">Cancelar</button>
          <div className="flex-1" />
          <button onClick={save} disabled={saving} data-testid="perfil-edit-save"
            className="inline-flex items-center gap-2 bg-[#1F2547] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#161A33] disabled:opacity-60">
            <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.new_password.length < 8) { toast.error("La nueva contraseña debe tener al menos 8 caracteres."); return; }
    if (form.new_password !== form.confirm) { toast.error("Las contraseñas no coinciden."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Contraseña actualizada");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4" data-testid="perfil-password-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E1E5F0] flex items-center justify-between bg-[#1F2547] text-white">
          <h3 className="font-display text-lg font-bold tracking-tight inline-flex items-center gap-2"><KeyRound size={16} /> Cambiar contraseña</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="perfil-password-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {[
            { k: "current_password", l: "Contraseña actual" },
            { k: "new_password", l: "Nueva contraseña (mín. 8)" },
            { k: "confirm", l: "Confirmar nueva contraseña" },
          ].map((f) => (
            <label key={f.k} className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6E7591]">{f.l}</span>
              <input type="password" value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`pwd-${f.k}`}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[#E1E5F0] focus:border-[#1F2547] outline-none text-sm" />
            </label>
          ))}
        </div>
        <div className="border-t border-[#E1E5F0] px-5 py-3 flex items-center gap-3">
          <button onClick={onClose} className="border border-[#E1E5F0] text-[#1F2547] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#F7F8FA]" data-testid="pwd-cancel">Cancelar</button>
          <div className="flex-1" />
          <button onClick={save} disabled={saving} data-testid="pwd-save"
            className="inline-flex items-center gap-2 bg-[#1F2547] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#161A33] disabled:opacity-60">
            <Save size={14} /> {saving ? "Guardando…" : "Actualizar contraseña"}
          </button>
        </div>
      </div>
    </div>
  );
}
